import React, { Fragment, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import {
  fetchCorporateEvents,
  fetchGlobalAgencyEvents,
  fetchOwnManagedEvents,
  fetchPublicEvents,
  unloadBookedEvents,
  unloadGlobalEvents,
  unloadOwnEvents,
  unloadPublicEvents,
} from "../../../redux/actions";
import { token, user, userAgencyObject } from "../../../modules/auth";
import EventWrapper from "../../../pages/admin/corporate/calendar-events/EventWrapper";
import Loading from "../../Loading";
import {
  mapBookedEventToCalendarEvent,
  mapEventToCalendarEvent,
} from "../../../pages/admin/corporate/calendar-events/mapEvents";
import { ButtonBrand, ButtonTertiary } from "../../Button";
import * as ROUTES from "../../../Router/corporate_routes";
import { Link } from "react-router-dom";
import AgencyCalendarDashFilters from "./AgencyCalendarDashFilters";
import { BOOKED_EVENT_STATUS } from "../../../constants";
import ViewEventModal from "../ViewEventModal";
import ViewBookedEvent from "../ViewBookedEvent";
import { canAccessAgencyCalendar } from "../../../modules/permissions";

function AgencyEventsCalendarDash({
  fetchPublicEvents,
  fetchGlobalAgencyEvents,
  publicEvents,
  globalEvents,
  ownEvents,
  token,
  fetchOwnManagedEvents,
  agency,
  isFetchingBookedEvents,
  newEventModal = false,
  unloadGlobalEvents,
  unloadBookedEvents,
  unloadPublicEvents,
  unloadOwnEvents,
  fetchCorporateEvents,
  user,
  bookedEvents,
  agencies,
  canAccessAgencyCalendar,
  selectedAgencies,
}) {
  const [event, setEvent] = useState(null);
  const [client, setClient] = useState(user?.id || null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showBookedEventModal, setShowBookedEventModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState("ownEvents");
  const [newEvent, setNewEvent] = useState(newEventModal);
  const currentDate = new Date();
  const [dateRangeForFetch, setDateRangeForFetch] = useState([
    new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
      .toISOString()
      .split("T")[0],
    new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0)
      .toISOString()
      .split("T")[0],
  ]);
  const localizer = momentLocalizer(moment);

  useEffect(() => {
    fetchPublicEvents(token, "global");
    if (agency) {
      fetchOwnEventsHandler();
      fetchGlobalAgencyEvents(token, agency.id);
    }
  }, [selectedAgencies]);

  useEffect(() => {
    if (activeFilter === "bookedEvents" || activeFilter === "all") {
      fetchCorporateEventsHandler();
    }
  }, [dateRangeForFetch, activeFilter, agencies]);

  const fetchOwnEventsHandler = () => {
    unloadEvents();

    if (agency) {
      fetchPublicEvents(token, "global");
      fetchGlobalAgencyEvents(token, agency.id);
    }

    let queryParams = {};
    queryParams.eventStatus = [
      BOOKED_EVENT_STATUS.CONFIRMED,
      BOOKED_EVENT_STATUS.PENDING_INVOICE,
      BOOKED_EVENT_STATUS.COMPLETED,
    ];
    return fetchOwnManagedEvents(token, queryParams);
  };

  const fetchCorporateEventsHandler = () => {
    unloadEvents();

    if (agency) {
      fetchPublicEvents(token, "global");
      fetchGlobalAgencyEvents(token, agency.id);
    }

    let queryParams = { dateRange: dateRangeForFetch };
    if (agency && Object.keys(agency).length > 0) {
      if (agencies && agencies.length > 0) {
        queryParams.agency = agencies.map(a => a.id).join(",");
      }
      queryParams.eventStatus = [
        BOOKED_EVENT_STATUS.CONFIRMED,
        BOOKED_EVENT_STATUS.PENDING_INVOICE,
        BOOKED_EVENT_STATUS.COMPLETED,
      ];
      // if (client) {
      //   queryParams.client = client;
      // }
      return fetchCorporateEvents(token, queryParams);
    }
  };

  const unloadEvents = useCallback(() => {
    unloadGlobalEvents();
    unloadBookedEvents();
    unloadPublicEvents();
    unloadOwnEvents();
  }, [
    unloadGlobalEvents,
    unloadBookedEvents,
    unloadPublicEvents,
    unloadOwnEvents,
  ]);

  let eventsArray = [];
  let publicEventArray = publicEvents
    ?.filter(
      publicEvent =>
        publicEvent.visibility_type !== "PUBLIC.AGENCY" &&
        publicEvent.visibility_type !== "PUBLIC.CORPORATE"
    )
    .map(publicEvent => {
      return mapEventToCalendarEvent(publicEvent, "public");
    });
  let globalEventArray = globalEvents?.map(globalEvent => {
    return mapEventToCalendarEvent(globalEvent, "global agency");
  });
  let bookedEventsArray = bookedEvents?.map(globalEvent => {
    return mapBookedEventToCalendarEvent(globalEvent, "bookedEvents");
  });
  let ownEventsArray = ownEvents?.map(ownEvent => {
    return mapBookedEventToCalendarEvent(ownEvent);
  });

  Array.prototype.push.apply(eventsArray, globalEventArray);
  Array.prototype.push.apply(eventsArray, publicEventArray);
  Array.prototype.push.apply(eventsArray, ownEventsArray);
  Array.prototype.push.apply(eventsArray, bookedEventsArray);

  const formats = {
    monthHeaderFormat: (date, culture, localizer) =>
      localizer.format(date, "MMM yyyy"),
  };

  const eventFiltersHandler = filterName => {
    setActiveFilter(filterName);
    if (filterName === "ownEvents") {
      fetchOwnEventsHandler();
    } else if (filterName === "bookedEvents") {
      fetchCorporateEventsHandler();
    }
  };

  const handleEventModalClose = () => {
    setShowBookedEventModal(false);
    setShowEventModal(false);
  };

  const handleSelectEvent = event => {
    if (event.bookedEvent) {
      setShowBookedEventModal(true);
    } else {
      setShowEventModal(true);
    }
    setEvent(event);
  };

  const handleSelectSlot = ({ start, end }) => {
    setEvent({ start, end });
    setNewEvent(true);
  };

  const handleNavigate = useCallback(
    (date, view) => {
      const givenDate = new Date(date);
      const lastMonth = new Date(
        givenDate.getFullYear(),
        givenDate.getMonth() - 1,
        1
      );
      const nextMonth = new Date(
        givenDate.getFullYear(),
        givenDate.getMonth() + 2,
        0
      );
      setDateRangeForFetch([
        lastMonth.toISOString().split("T")[0],
        nextMonth.toISOString().split("T")[0],
      ]);
    },
    [dateRangeForFetch]
  );

  return (
    <Fragment key={`agencyEventsCalendarDash-${selectedAgencies?.length || 1}`}>
      <div className="relative mt-4">
        {isFetchingBookedEvents ? (
          <div className={"calendar-loader"}>
            <Loading />
          </div>
        ) : null}
        <Calendar
          className={"agency-calendar-dash"}
          formats={formats}
          views={["month"]}
          onNavigate={handleNavigate}
          popup
          events={eventsArray}
          onClick={{}}
          selectable
          onSelectEvent={event => handleSelectEvent(event)}
          onSelectSlot={handleSelectSlot}
          step={60}
          showMultiDayTimes
          defaultDate={new Date()}
          localizer={localizer}
          components={{
            event: EventWrapper,
            toolbar: props => {
              return (
                <>
                  <AgencyCalendarDashFilters
                    {...props}
                    activeFilter={activeFilter}
                    eventFiltersHandler={eventFiltersHandler}
                  />
                </>
              );
            },
          }}
        />

        {showEventModal && (
          <ViewEventModal event={event} onClose={handleEventModalClose} />
        )}
        {showBookedEventModal && (
          <ViewBookedEvent event={event} onClose={handleEventModalClose} />
        )}

        {canAccessAgencyCalendar ? (
          <div className="flex my-4 justify-start">
            <ButtonTertiary
              style={{ width: "inherit" }}
              classes="text-md font-bold mr-2"
            >
              <Link
                to={ROUTES.FRONTEND.AGENCY.CALENDAR}
                className={"text-white"}
              >
                View Full Agency Calendar
              </Link>
            </ButtonTertiary>
          </div>
        ) : null}
      </div>
    </Fragment>
  );
}

const mapStateToProps = state => {
  return {
    user: user(state),
    agency: userAgencyObject(state),
    ...state.selectedAgencies,
    ...state.globalEvents,
    ...state.publicEvents,
    ...state.bookedEvents,
    token: token(state),
    canAccessAgencyCalendar: canAccessAgencyCalendar(state.auth),
  };
};

const mapDispatchToProps = {
  unloadBookedEvents,
  fetchCorporateEvents,
  fetchOwnManagedEvents,
  fetchGlobalAgencyEvents,
  fetchPublicEvents,
  unloadGlobalEvents,
  unloadPublicEvents,
  unloadOwnEvents,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AgencyEventsCalendarDash);
